package com.arena_web.ui.components.notifications

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.toRgbString
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*

val ToastSlideInKeyframes = Keyframes {
    from {
        Modifier.translateX(100.percent).opacity(0)
    }
    to {
        Modifier.translateX(0.percent).opacity(1)
    }
}

@Composable
fun ToastContainer(state: ToastState) {
    Column(
        modifier = Modifier
            .position(Position.Fixed)
            .sTop(16)
            .sRight(16)
            .zIndex(2000)
            .sGap(8)
            .pointerEvents(PointerEvents.None)
            .styleModifier {
                property("max-width", s(400))
                property("width", "100%")
            }
    ) {
        state.toasts.forEach { toast ->
            key(toast.id) {
                ToastItem(toast, onDismiss = { state.dismiss(toast.id) })
            }
        }
    }
}

@Composable
fun ToastItem(toast: ToastData, onDismiss: () -> Unit) {
    LaunchedEffect(toast.id) {
        delay(toast.duration)
        onDismiss()
    }

    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    val (accentColor, icon) = when (toast.type) {
        ToastType.SUCCESS -> Color.rgb(34, 197, 94) to "\u2713"
        ToastType.ERROR -> AppColors.Error to "\u2717"
        ToastType.WARNING -> Color.rgb(245, 158, 11) to "\u26A0"
        ToastType.INFO -> AppColors.Primary to "\u2139"
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(colors.surface)
            .sBorderRadius(12)
            .sPadding(12, 16)
            .pointerEvents(PointerEvents.Auto)
            .cursor(Cursor.Pointer)
            .onClick { onDismiss() }
            .animation(
                ToastSlideInKeyframes.toAnimation(
                    duration = Constants.ANIMATION_NORMAL.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            )
            .styleModifier {
                property("box-shadow", "0 4px 16px rgba(0, 0, 0, 0.2)")
                property("border-left", "4px solid ${accentColor.toRgbString()}")
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Start
    ) {
        Box(
            modifier = Modifier
                .sSize(24)
                .borderRadius(50.percent)
                .backgroundColor(accentColor)
                .sMarginRight(12),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                icon,
                modifier = Modifier
                    .sFontSize(12)
                    .fontWeight(FontWeight.Bold)
                    .color(Color.rgb(255, 255, 255))
            )
        }

        SpanText(
            text = toast.message,
            modifier = Modifier
                .sFontSize(14)
                .color(colors.textPrimary)
        )
    }
}
